import type { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'
import { siteConfig } from '#app/utils/constants/brand'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'

export const ROUTE_PATH = '/inbound-email-api' as const

const TITLE = 'Inbound Email API: Receive Incoming Email as JSON | Inbox Bridge'
const DESCRIPTION =
  'Use the Inbox Bridge inbound email API to receive incoming email as JSON. Get an email address, receive a signed webhook, and parse the payload with typed code.'
const URL = `${siteConfig.siteUrl}${ROUTE_PATH}`

// Pinned manually so dateModified only changes when the content actually does.
// Bump dateModified when you make substantive edits to the page body.
const DATE_PUBLISHED = '2026-07-20'
const DATE_MODIFIED = '2026-07-20'

const ARTICLE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: 'Inbound Email API: receive incoming email as JSON',
  description: DESCRIPTION,
  image: siteConfig.siteImageUrl,
  url: URL,
  datePublished: DATE_PUBLISHED,
  dateModified: DATE_MODIFIED,
  author: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
    logo: { '@type': 'ImageObject', url: siteConfig.siteImageUrl },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': URL },
}

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.siteUrl },
    { '@type': 'ListItem', position: 2, name: 'Inbound Email API', item: URL },
  ],
}

export const meta: MetaFunction = () => {
  return [
    { title: TITLE },
    { name: 'description', content: DESCRIPTION },
    { tagName: 'link', rel: 'canonical', href: URL },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: URL },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: siteConfig.siteImageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { 'script:ld+json': ARTICLE_JSON_LD },
    { 'script:ld+json': BREADCRUMB_JSON_LD },
  ]
}

const codeBlockClass =
  'overflow-x-auto rounded-lg border border-border bg-secondary/40 p-4 text-sm text-muted-foreground'

export default function InboundEmailApiPage() {
  return (
    <main className="mx-auto flex w-full max-w-screen-md flex-col gap-8 px-6 py-20">
      <header className="flex flex-col gap-4">
        <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Developers
        </p>
        <h1 className="text-4xl font-bold tracking-tight text-primary md:text-5xl">
          Inbound Email API
        </h1>
        <p className="text-lg leading-relaxed text-muted-foreground">
          An inbound email API lets your application receive email. You do not operate a
          mail server. You do not poll an inbox. Inbox Bridge receives the email, parses
          it, and sends the result to your endpoint as JSON.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          This page shows the full API surface: the address you receive email at, the
          JSON payload, the delivery headers, signature verification, retries, and
          replay.
        </p>
      </header>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          What is an inbound email API?
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Most email APIs send email. An inbound email API does the opposite. It receives
          incoming email for your application and converts each message into structured
          data.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          With Inbox Bridge, the API is push-based. You do not call our servers to get new
          messages. When an email arrives, we call your server. Each email becomes one
          HTTPS POST request with a JSON body. Your application handles it like any other
          API request.
        </p>
        <p className="leading-relaxed text-muted-foreground">The flow has four steps:</p>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>You create a bridge and get a unique email address</li>
          <li>Someone sends an email to that address</li>
          <li>Inbox Bridge parses the message into JSON</li>
          <li>Inbox Bridge sends the JSON to your webhook URL</li>
        </ol>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Get an inbound email address
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Each bridge has one unique address. The address looks like this:
        </p>
        <pre className={codeBlockClass}>{`abc123@webhook.inboxbridge.io`}</pre>
        <p className="leading-relaxed text-muted-foreground">
          Give this address to a person, a device, a vendor system, or a form. All email
          sent to the address goes to your endpoint. You can create more bridges to route
          different senders to different endpoints.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">The JSON payload</h2>
        <p className="leading-relaxed text-muted-foreground">
          Each delivery is an HTTPS POST request with{' '}
          <code>Content-Type: application/json</code>. The body contains the parsed
          email:
        </p>
        <pre className={codeBlockClass}>
          {`{
  "id": "9b2c1f7a4e",
  "receivedAt": "2026-07-20T15:04:22.000Z",
  "from": { "email": "customer@example.com", "name": "Jordan Lee" },
  "to": [{ "email": "abc123@webhook.inboxbridge.io", "name": "" }],
  "cc": [],
  "bcc": [],
  "replyTo": null,
  "subject": "Question about my order",
  "date": "2026-07-20T15:04:00.000Z",
  "textBody": "Hey, can someone check order #1234?",
  "htmlBody": "<p>Hey, can someone check order #1234?</p>",
  "attachments": [
    {
      "name": "invoice.pdf",
      "contentType": "application/pdf",
      "contentLength": 482931,
      "contentId": null,
      "downloadUrl": "https://inboxbridge.io/attachments/clx1a2b3?token=…"
    }
  ]
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">The important fields are:</p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>
            <code>id</code>: a stable identifier for the message. Use it to prevent
            duplicate processing.
          </li>
          <li>
            <code>from</code>, <code>to</code>, <code>cc</code>, <code>bcc</code>:
            contact objects with an <code>email</code> and a <code>name</code>.
          </li>
          <li>
            <code>textBody</code> and <code>htmlBody</code>: the message body in both
            forms.
          </li>
          <li>
            <code>attachments</code>: one entry for each file, with its name, content
            type, and size. On paid plans, each entry also has a signed{' '}
            <code>downloadUrl</code>.
          </li>
        </ul>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Delivery headers</h2>
        <p className="leading-relaxed text-muted-foreground">
          Each request includes two headers. Use them to make sure the request came from
          Inbox Bridge:
        </p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>
            <code>X-InboxBridge-Signature</code>: an HMAC-SHA256 of the raw request body,
            in the form <code>sha256=…</code>. The key is the signing secret for your
            bridge.
          </li>
          <li>
            <code>X-InboxBridge-Timestamp</code>: the delivery time as unix seconds. Use
            it to reject old requests.
          </li>
        </ul>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Verify and parse with typed code
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Our open-source package,{' '}
          <a
            href="https://www.npmjs.com/package/inbound-email-webhook"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            inbound-email-webhook
          </a>
          , does the verification and the parsing for you. It checks the signature with a
          constant-time compare. It validates the payload against a schema. It returns a
          fully typed email object. It is the same code Inbox Bridge uses in production.
        </p>
        <pre className={codeBlockClass}>{`npm install inbound-email-webhook`}</pre>
        <p className="leading-relaxed text-muted-foreground">
          In Next.js, one function call handles the full request:
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import { verifyRequest } from "inbound-email-webhook/next";

export const runtime = "nodejs";

export async function POST(request: Request) {
  const result = await verifyRequest(request, {
    secret: process.env.INBOXBRIDGE_SIGNING_SECRET!,
  });

  if (!result.ok) return result.response;

  const email = result.email; // typed InboundEmail
  console.log(email.from.email, email.subject);

  return Response.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          The package also has an Express middleware at{' '}
          <code>inbound-email-webhook/express</code> and a plain <code>verify</code>{' '}
          function for other frameworks. If you prefer no dependency, you can compute the
          HMAC yourself. The{' '}
          <Link
            to="/guides/receive-inbound-email-as-json-nextjs"
            prefetch="intent"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            Next.js guide
          </Link>{' '}
          shows the manual version.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Responses and retries</h2>
        <p className="leading-relaxed text-muted-foreground">
          Return a <code>2xx</code> status to accept a delivery. Keep the handler fast.
          Store the payload first. Do slow work in the background.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          If your endpoint returns an error, or does not respond, Inbox Bridge retries the
          delivery. Retries use exponential backoff. The number of attempts follows your
          plan limit. Each attempt is recorded with its response code and duration, so you
          can see what happened.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          You can also replay any delivery from the dashboard. Replay sends the same
          payload again. This helps when you debug your handler.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Because retries exist, the same message can arrive more than one time. Use the
          payload <code>id</code> as an idempotency key. Store it with a unique
          constraint, and skip messages you already processed.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Do I need an email inbox API instead?
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Some teams look for an email inbox API. That model connects to a mailbox over
          IMAP or a provider API, and your application polls for new messages. It works,
          but it adds moving parts: credentials, polling jobs, folder state, and processed
          message tracking.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          If the goal is to react to incoming email inside your application, a push-based
          incoming email API is simpler. The email arrives. Your endpoint gets one JSON
          request. There is no polling and no mailbox state.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Use an inbox API when you must read an existing mailbox you do not control. Use
          an inbound email API when you can give out a new address and want each message
          to become an event in your app.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Quick start</h2>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Create a free account and add a bridge</li>
          <li>Paste your webhook URL</li>
          <li>Copy the generated email address</li>
          <li>Send a test email to the address</li>
          <li>Watch the JSON request arrive at your endpoint</li>
        </ol>
        <p className="leading-relaxed text-muted-foreground">
          The free plan includes a working bridge, so you can test the full flow before
          you pay for anything.
        </p>
      </section>

      <div className="flex flex-wrap gap-3">
        <Link
          to={LOGIN_PATH}
          className={cn(buttonVariants({ size: 'lg' }), 'gap-2 font-semibold')}>
          Get your inbound email address
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/email-to-webhook"
          className={cn(buttonVariants({ variant: 'outline', size: 'lg' }))}>
          Feature overview
        </Link>
      </div>

      <section className="mt-8 border-t border-border/40 pt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">
          Related
        </h2>
        <ul className="flex flex-col gap-2 text-base text-muted-foreground">
          <li>
            <Link
              to="/guides/receive-inbound-email-as-json-nextjs"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Receive inbound email as JSON in Next.js
            </Link>
            . A full build with route handlers, storage, and idempotency.
          </li>
          <li>
            <Link
              to="/guides/inbound-email-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Inbound email webhook tutorial
            </Link>
            . The five-minute version that is not tied to a framework.
          </li>
          <li>
            <Link
              to="/email-to-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Email to webhook overview
            </Link>
            . What the service does and what payloads look like.
          </li>
          <li>
            <Link
              to="/cloudmailin-alternative"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              CloudMailin alternative
            </Link>
            . Feature comparison if you are evaluating providers.
          </li>
        </ul>
      </section>
    </main>
  )
}
